import React, { useState } from 'react';
import { Search, Eye, Copy, Send, Zap } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { NotificationRecord } from '@/services/PagesServices/MarketingAndNotificationService';
import './NotificationHistory.css';

interface NotificationHistoryProps {
  onViewNotification: (notification: NotificationRecord) => void;
}

const NotificationHistory: React.FC<NotificationHistoryProps> = ({ onViewNotification }) => {
  const { notificationHistory } = useMarketingAndNotificationStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const getTypeLabel = (type: string) => {
    if (type === 'email') return 'Email';
    if (type === 'in-app') return 'In-App';
    return 'Both';
  };

  const getStatusColor = (status: string) => {
    if (status === 'sent') return '#10b981';
    if (status === 'scheduled') return '#3b82f6';
    return '#f59e0b';
  };

  const filteredNotifications = notificationHistory?.filter(notif => {
    const matchesSearch = notif.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || notif.type === filterType;
    const matchesStatus = filterStatus === 'all' || notif.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  }) || [];

  return (
    <div className="admin-marketing-history-content">
      <div className="admin-marketing-history-header">
        <div>
          <h2 className="admin-marketing-section-title">Notification History</h2>
          <p className="admin-marketing-section-subtitle">View all sent and scheduled notifications</p>
        </div>
        <button className="admin-marketing-new-btn">
          <span>+</span>
          New Notification
        </button>
      </div>

      <div className="admin-marketing-filters">
        <div className="admin-marketing-search-box">
          <Search size={18} className="admin-marketing-search-icon" />
          <input
            type="text"
            className="admin-marketing-search-input"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="admin-marketing-filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Type</option>
          <option value="email">Email</option>
          <option value="in-app">In-App</option>
          <option value="both">Both</option>
        </select>
        <select
          className="admin-marketing-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="sent">Sent</option>
          <option value="scheduled">Scheduled</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className="admin-marketing-table-container">
        <table className="admin-marketing-history-table">
          <thead>
            <tr>
              <th>NOTIFICATION</th>
              <th>TYPE</th>
              <th>AUDIENCE</th>
              <th>STATUS</th>
              <th>DATE</th>
              <th>PERFORMANCE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredNotifications.map(notif => (
              <tr key={notif.id}>
                <td>
                  <div className="admin-marketing-notif-cell">
                    <div className="admin-marketing-notif-icon">
                      <Zap size={18} />
                    </div>
                    <div>
                      <div className="admin-marketing-notif-title">{notif.title}</div>
                      <div className="admin-marketing-notif-subtitle">{notif.subtitle}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="admin-marketing-type-badge">{getTypeLabel(notif.type)}</span>
                </td>
                <td>
                  <div className="admin-marketing-audience-cell">
                    {notif.audiences.map((aud, idx) => (
                      <div key={idx} className="admin-marketing-audience-tag">{aud}</div>
                    ))}
                  </div>
                </td>
                <td>
                  <span
                    className="admin-marketing-status-badge"
                    style={{ backgroundColor: getStatusColor(notif.status) + '20', color: getStatusColor(notif.status) }}
                  >
                    {notif.status.charAt(0).toUpperCase() + notif.status.slice(1)}
                  </span>
                </td>
                <td>
                  <div className="admin-marketing-date-cell">
                    <div>{notif.date || '-'}</div>
                    <div className="admin-marketing-date-label">{notif.dateLabel}</div>
                  </div>
                </td>
                <td>
                  {notif.openRate ? (
                    <div className="admin-marketing-performance-cell">
                      <div className="admin-marketing-metric">
                        <Eye size={14} />
                        <span>{notif.openRate}%</span>
                      </div>
                      <div className="admin-marketing-metric">
                        <Zap size={14} />
                        <span>{notif.clickRate}%</span>
                      </div>
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td>
                  <div className="admin-marketing-actions">
                    <button
                      className="admin-marketing-action-btn"
                      onClick={() => onViewNotification(notif)}
                      title="View"
                    >
                      <Eye size={16} />
                    </button>
                    <button className="admin-marketing-action-btn" title="Copy">
                      <Copy size={16} />
                    </button>
                    <button className="admin-marketing-action-btn" title="Send">
                      <Send size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NotificationHistory;
